import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Send, Copy, Trash2, Plus, Users2 } from "lucide-react";

export default function PortalUsersAdmin() {
    const [items, setItems] = useState([]);
    const [projects, setProjects] = useState([]);
    const [investors, setInvestors] = useState([]);
    const [lenders, setLenders] = useState([]);
    const [showNew, setShowNew] = useState(false);
    const [outbox, setOutbox] = useState([]);

    const load = async () => {
        const [pu, pr, iv, ln, ob] = await Promise.all([
            api.get("/portal-users"), api.get("/projects"),
            api.get("/investors"), api.get("/lenders"),
            api.get("/email-outbox"),
        ]);
        setItems(pu.data.items || []);
        setProjects(pr.data.items || []);
        setInvestors(iv.data.items || []);
        setLenders(ln.data.items || []);
        setOutbox(ob.data.items || []);
    };
    useEffect(() => { load(); }, []);

    const issueLink = async (id) => {
        try {
            const { data } = await api.post(`/portal-users/${id}/magic-link`);
            const fullUrl = `${window.location.origin}${data.magic_link_path}`;
            await navigator.clipboard.writeText(fullUrl).catch(() => {});
            toast.success("Magic link generated and copied");
            load();
        } catch { toast.error("Failed"); }
    };

    const deactivate = async (id) => {
        if (!window.confirm("Deactivate this portal user?")) return;
        await api.delete(`/portal-users/${id}`);
        toast.success("Deactivated");
        load();
    };

    return (
        <div className="p-6" data-testid="portal-users-admin">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Portal users</div>
                    <h1 className="text-2xl tracking-tight mt-1">Stakeholder access</h1>
                </div>
                <button onClick={() => setShowNew(!showNew)} className="border border-border px-3 py-2 text-xs flex items-center gap-1" data-testid="new-portal-user-btn">
                    <Plus size={12} /> New portal user
                </button>
            </div>

            {showNew && <NewForm projects={projects} investors={investors} lenders={lenders} onDone={() => { setShowNew(false); load(); }} />}

            <div className="border border-border overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-secondary/40">
                        <tr>
                            <th className="text-left p-2 font-normal text-xs uppercase tracking-widest text-muted-foreground">Email</th>
                            <th className="text-left p-2 font-normal text-xs uppercase tracking-widest text-muted-foreground">Type</th>
                            <th className="text-left p-2 font-normal text-xs uppercase tracking-widest text-muted-foreground">Scope</th>
                            <th className="text-left p-2 font-normal text-xs uppercase tracking-widest text-muted-foreground">Last login</th>
                            <th className="text-left p-2 font-normal text-xs uppercase tracking-widest text-muted-foreground">Status</th>
                            <th className="text-right p-2 font-normal text-xs uppercase tracking-widest text-muted-foreground">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {items.map((pu) => (
                            <tr key={pu.id} data-testid={`portal-user-row-${pu.id}`}>
                                <td className="p-2">{pu.email}<div className="text-xs text-muted-foreground">{pu.display_name}</div></td>
                                <td className="p-2 capitalize">{pu.portal_type}</td>
                                <td className="p-2 text-xs">
                                    {pu.investor_id && <div>Investor scoped</div>}
                                    {pu.lender_id && <div>Lender scoped</div>}
                                    {pu.project_ids?.length ? <div>{pu.project_ids.length} project(s)</div> : null}
                                </td>
                                <td className="p-2 text-xs text-muted-foreground">{pu.last_login_at ? pu.last_login_at.slice(0, 16).replace("T", " ") : "Never"}</td>
                                <td className="p-2 text-xs">{pu.is_active ? "Active" : "Inactive"}</td>
                                <td className="p-2 text-right">
                                    <button onClick={() => issueLink(pu.id)} className="text-xs underline mr-3" data-testid={`issue-link-${pu.id}`}>Send link</button>
                                    <button onClick={() => deactivate(pu.id)} className="text-xs underline text-red-600"><Trash2 size={10} className="inline" /></button>
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr><td colSpan={6} className="p-6 text-center text-sm text-muted-foreground">No portal users yet. Invite investors, lenders, clients, or subcontractors.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="mt-8">
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Recent mock email outbox (last 20)</div>
                <div className="border border-border overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-secondary/40">
                            <tr><th className="text-left p-2 font-normal">Time</th><th className="text-left p-2 font-normal">Kind</th><th className="text-left p-2 font-normal">To</th><th className="text-left p-2 font-normal">Subject</th><th className="text-left p-2 font-normal">Status</th><th className="text-left p-2 font-normal">Link</th></tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {outbox.slice(0, 20).map((m) => (
                                <tr key={m.id}>
                                    <td className="p-2 text-muted-foreground">{m.created_at?.slice(0, 16).replace("T", " ")}</td>
                                    <td className="p-2">{m.kind}</td>
                                    <td className="p-2">{m.to_email}</td>
                                    <td className="p-2">{m.subject}</td>
                                    <td className="p-2">{m.status}</td>
                                    <td className="p-2">
                                        {m.magic_link_url && (
                                            <button onClick={() => {
                                                const url = `${window.location.origin}${m.magic_link_url}`;
                                                navigator.clipboard.writeText(url);
                                                toast.success("Link copied");
                                            }} className="underline flex items-center gap-1"><Copy size={10} /> Copy</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {outbox.length === 0 && <tr><td colSpan={6} className="p-4 text-center text-muted-foreground">No emails yet.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function NewForm({ projects, investors, lenders, onDone }) {
    const [form, setForm] = useState({
        email: "", display_name: "", portal_type: "client",
        project_ids: [], investor_id: "", lender_id: "", show_financials: false,
    });
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = { ...form };
            if (!payload.investor_id) delete payload.investor_id;
            if (!payload.lender_id) delete payload.lender_id;
            await api.post("/portal-users", payload);
            toast.success("Portal user created");
            onDone();
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
        setBusy(false);
    };

    const toggleProj = (id) => setForm((f) => ({ ...f, project_ids: f.project_ids.includes(id) ? f.project_ids.filter((x) => x !== id) : [...f.project_ids, id] }));

    return (
        <form onSubmit={submit} className="border border-border p-4 mb-4 grid md:grid-cols-2 gap-3" data-testid="portal-user-new-form">
            <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground">Email</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full border border-border bg-background px-3 py-2 text-sm" data-testid="new-pu-email" />
            </div>
            <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground">Display name</label>
                <input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className="mt-1 w-full border border-border bg-background px-3 py-2 text-sm" />
            </div>
            <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground">Portal type</label>
                <select value={form.portal_type} onChange={(e) => setForm({ ...form, portal_type: e.target.value })} className="mt-1 w-full border border-border bg-background px-3 py-2 text-sm" data-testid="new-pu-type">
                    <option value="client">Client</option>
                    <option value="investor">Investor</option>
                    <option value="lender">Lender</option>
                    <option value="subcontractor">Subcontractor</option>
                </select>
            </div>
            {form.portal_type === "investor" && (
                <div>
                    <label className="text-xs uppercase tracking-widest text-muted-foreground">Linked investor</label>
                    <select required value={form.investor_id} onChange={(e) => setForm({ ...form, investor_id: e.target.value })} className="mt-1 w-full border border-border bg-background px-3 py-2 text-sm" data-testid="new-pu-investor">
                        <option value="">Select…</option>
                        {investors.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                    </select>
                </div>
            )}
            {form.portal_type === "lender" && (
                <div>
                    <label className="text-xs uppercase tracking-widest text-muted-foreground">Linked lender</label>
                    <select required value={form.lender_id} onChange={(e) => setForm({ ...form, lender_id: e.target.value })} className="mt-1 w-full border border-border bg-background px-3 py-2 text-sm">
                        <option value="">Select…</option>
                        {lenders.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                </div>
            )}
            <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-widest text-muted-foreground">Scoped projects {form.portal_type === "client" ? "(required)" : "(optional)"}</label>
                <div className="mt-1 grid md:grid-cols-2 gap-1 max-h-48 overflow-y-auto border border-border p-2">
                    {projects.map((p) => (
                        <label key={p.id} className="flex items-center gap-2 text-xs cursor-pointer">
                            <input type="checkbox" checked={form.project_ids.includes(p.id)} onChange={() => toggleProj(p.id)} />
                            <span>{p.name}{p.is_demo ? " · demo" : ""}</span>
                        </label>
                    ))}
                </div>
            </div>
            {form.portal_type === "client" && (
                <label className="flex items-center gap-2 text-xs md:col-span-2">
                    <input type="checkbox" checked={form.show_financials} onChange={(e) => setForm({ ...form, show_financials: e.target.checked })} />
                    Allow this client to see project financials
                </label>
            )}
            <div className="md:col-span-2 flex justify-end gap-2">
                <button type="button" onClick={onDone} className="border border-border px-3 py-2 text-xs">Cancel</button>
                <button disabled={busy} className="bg-foreground text-background px-3 py-2 text-xs" data-testid="new-pu-submit">{busy ? "Creating…" : "Create"}</button>
            </div>
        </form>
    );
}
